import { BUSINESS_TYPE } from "@faire/retailer-visitor-shared/consts/BUSINESS_TYPE";
import { PersonalInfoData } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { getSettingMarketingSMSEligibleCountries } from "@faire/retailer-visitor-shared/settings/getSettingMarketingSMSEligibleCountries";
import { useBuildSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useCreateUserAndRetailer";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web--source/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { useMutation as useSaveIncompleteSignupAttempt } from "@faire/web-api--source/endpoints/www/api/user/retailer-signup-attempt/post-hooks";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";

export const useSaveIncompleteSignup = () => {
  const { mutateAsync: saveIncompleteSignupAttempt, isPending: isLoading } =
    useSaveIncompleteSignupAttempt();
  const { search } = useLocation();
  const { buildSignUpPayload } = useBuildSignUpRequest();

  const saveIncompleteSignupAfterPersonalStep = useCallback(
    async (values: PersonalInfoData): Promise<Partial<ISignUpRequest>> => {
      const user = IUser.build({
        ...values,
        locale_key: values.locale_key,
      });
      delete user.password;
      const payload = ISignUpRequest.build({
        user,
        country: values.country,
      });

      const formOrSessionCountry = values.country ?? getSessionCountry();

      const isEligibleCountryForMarketingEmailOptOut =
        formOrSessionCountry !== THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;

      const isEligibleCountryForMarketingSMS =
        getSettingMarketingSMSEligibleCountries().some(
          (eligibleCountry) => eligibleCountry === formOrSessionCountry
        );

      let marketingEmailPreference = ISignUpRequest.EmailPreference.OPTED_IN;

      if (
        isEligibleCountryForMarketingEmailOptOut &&
        !values.optedInToMarketingEmails
      ) {
        marketingEmailPreference = ISignUpRequest.EmailPreference.OPTED_OUT;
      } else if (search.includes("brand-invoice")) {
        marketingEmailPreference = ISignUpRequest.EmailPreference.UNDECIDED;
      }

      let marketingSMSPreference =
        ISignUpRequest.SmsPreference.SMS_PREFERENCE_UNDECIDED;

      if (isEligibleCountryForMarketingSMS) {
        marketingSMSPreference = values.receiveMarketingSMSs
          ? ISignUpRequest.SmsPreference.SMS_PREFERENCE_OPTED_IN
          : ISignUpRequest.SmsPreference.SMS_PREFERENCE_OPTED_OUT;
      }

      try {
        await saveIncompleteSignupAttempt([payload]);
      } catch (error) {
        // error handling by BE; background network task, user shouldn't know
        // this request failed
      }

      return {
        user: IUser.build({
          ...values,
        }),
        country: values.country,
        marketing_email_preference: marketingEmailPreference,
        marketing_sms_preference: marketingSMSPreference,
      };
    },
    [saveIncompleteSignupAttempt, search]
  );

  const saveIncompleteSignupIfConsumer = useCallback(async () => {
    const { user, country } = buildSignUpPayload();
    const userPayload = IUser.build({
      ...user,
    });
    delete userPayload.password;
    try {
      const payload = ISignUpRequest.build({
        user: userPayload,
        country,
        business_type: BUSINESS_TYPE.CONSUMER,
      });
      await saveIncompleteSignupAttempt([payload]);
    } catch (error) {
      // error handling by BE; background network task, user shouldn't know
      // this request failed
    }
  }, [buildSignUpPayload, saveIncompleteSignupAttempt]);

  return {
    saveIncompleteSignupAfterPersonalStep,
    saveIncompleteSignupIfConsumer,
    isLoading,
  };
};
